import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, CalendarCheck2, AlarmClockOff, Check, MapPinned } from "lucide-react";
import { api } from "@/lib/api";
import { addDays, fmtLocal, fmtTime, startOfDay, todayStr, ymd } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import CalendarView, { rangeFor } from "@/components/CalendarView";
import TaskDialog from "@/components/TaskDialog";
import RescheduleDialog, { VisitHistory } from "@/components/RescheduleDialog";
import { ClientPhone, RescheduleTaskButton } from "@/components/TaskExtras";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const VIEWS = ["day", "week", "month"];

export default function TasksPage() {
  const [params, setParams] = useSearchParams();
  const panel = params.get("view") === "pending" ? "pending" : "today";
  const [view, setView] = useState(() => (window.innerWidth < 768 ? "day" : "week"));
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [dialog, setDialog] = useState(null); // {task} | {defaultDue}
  const [rescheduling, setRescheduling] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [ownerId, setOwnerId] = useState("all");
  const owner = isAdmin && ownerId !== "all" ? { owner_id: ownerId } : {};
  const usersQ = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: isAdmin,
    staleTime: 5 * 60_000,
  });

  const [s, e] = rangeFor(view, anchor);
  const today = todayStr();
  const calQ = useQuery({
    queryKey: ["tasks", "range", ymd(s), ymd(e), ownerId],
    queryFn: async () => (await api.get("/tasks", { params: { start: ymd(s), end: ymd(e), ...owner } })).data.items,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const listQ = (v) => ({
    queryKey: ["tasks", v, today, ownerId],
    queryFn: async () => (await api.get("/tasks", { params: { view: v, today, ...owner } })).data.items,
    staleTime: 30_000,
  });
  const todayQ = useQuery(listQ("today"));
  const pendingQ = useQuery(listQ("pending"));

  const step = (dir) => {
    if (view === "day") setAnchor((a) => addDays(a, dir));
    else if (view === "week") setAnchor((a) => addDays(a, 7 * dir));
    else setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + dir, 1));
  };
  const label = useMemo(() => {
    if (view === "month") return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    if (view === "day") return anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
    const last = addDays(s, 6);
    return `${s.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${last.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
  }, [view, anchor, s]);

  const defaultDue = `${ymd(anchor)}T${String(Math.min(21, new Date().getHours() + 1)).padStart(2, "0")}:00`;

  return (
    <div className="space-y-4" data-testid="tasks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-navy lg:text-3xl">Tasks</h2>
          <p className="mt-1 text-sm text-slate-500">
            {isAdmin ? "Every team member's site visits and follow-ups." : "Your site visits and follow-ups."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        {isAdmin && (
          <Select value={ownerId} onValueChange={setOwnerId}>
            <SelectTrigger data-testid="tasks-user-filter" className="h-11 w-52 rounded-full bg-white">
              <SelectValue placeholder="All team members" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="tasks-user-filter-all">All team members</SelectItem>
              {(usersQ.data || []).map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`tasks-user-filter-${u.id}`}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button data-testid="task-add-btn" onClick={() => setDialog({ defaultDue })} className="h-11 rounded-full bg-navy px-5 hover:brightness-110">
          <Plus className="h-4 w-4" /> New task
        </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-3 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <button data-testid="cal-prev" onClick={() => step(-1)} className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white hover:bg-slate-50" aria-label="Previous"><ChevronLeft className="h-4 w-4" /></button>
              <button data-testid="cal-today" onClick={() => setAnchor(startOfDay(new Date()))} className="h-9 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-navy hover:bg-slate-50">Today</button>
              <button data-testid="cal-next" onClick={() => step(1)} className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white hover:bg-slate-50" aria-label="Next"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="text-base font-bold text-navy" data-testid="cal-label">{label}</div>
            <div className="ml-auto flex rounded-full bg-slate-100 p-0.5" data-testid="cal-view-toggle">
              {VIEWS.map((v) => (
                <button
                  key={v}
                  data-testid={`cal-view-${v}`}
                  onClick={() => setView(v)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold capitalize transition-colors ${view === v ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <CalendarView
            view={view}
            anchor={anchor}
            tasks={calQ.data || []}
            onTaskClick={(task) => setDialog({ task })}
            onSlotClick={(due) => setDialog({ defaultDue: due })}
            onDayClick={(d) => { setAnchor(startOfDay(d)); setView("day"); }}
          />
          <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
            <Legend cls="border-[hsl(var(--brand-gold))] bg-amber-50" label="Site visit" />
            <Legend cls="border-[hsl(var(--brand-navy))] bg-blue-50" label="Task" />
            <Legend cls="border-rose-400 bg-rose-50" label="Overdue" />
            <Legend cls="border-slate-300 bg-slate-100" label="Done" />
          </div>
        </div>

        <aside className="soft-card h-fit p-3 lg:sticky lg:top-20" data-testid="tasks-side-panel">
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
            {[
              { key: "today", label: "Today", Icon: CalendarCheck2, n: todayQ.data?.length },
              { key: "pending", label: "Pending", Icon: AlarmClockOff, n: pendingQ.data?.length },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`tasks-panel-${t.key}`}
                onClick={() => setParams(t.key === "today" ? {} : { view: t.key }, { replace: true })}
                className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition-colors ${panel === t.key ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}
              >
                <t.Icon className={`h-3.5 w-3.5 ${t.key === "pending" && t.n ? "text-rose-500" : ""}`} /> {t.label}
                <span className="rounded-full bg-slate-200/80 px-1.5 text-[10px]">{t.n ?? "…"}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 px-1 text-[11px] text-slate-400">
            {panel === "today" ? "Open tasks due today." : "Open tasks from earlier days that were never completed."}
          </p>
          <TaskList items={(panel === "today" ? todayQ.data : pendingQ.data) || []} showDate={panel === "pending"} onOpen={(task) => setDialog({ task })} panel={panel} onReschedule={setRescheduling} />
        </aside>
      </div>

      <TaskDialog open={Boolean(dialog)} onOpenChange={(v) => !v && setDialog(null)} task={dialog?.task} defaultDue={dialog?.defaultDue}
        onReschedule={(t) => { setDialog(null); setRescheduling(t); }} />
      <RescheduleDialog task={rescheduling} onOpenChange={(v) => !v && setRescheduling(null)} />
    </div>
  );
}

function TaskList({ items, showDate, onOpen, panel, onReschedule }) {
  const qc = useQueryClient();
  const doneMut = useMutation({
    mutationFn: async (id) => api.patch(`/tasks/${id}`, { done: true }),
    onSuccess: () => { toast.success("Task completed"); ["tasks", "opportunities", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: () => toast.error("Update failed"),
  });
  if (!items.length) {
    return <div className="py-8 text-center text-sm text-slate-400" data-testid={`tasks-${panel}-empty`}>{panel === "today" ? "Nothing due today." : "No pending tasks — nice!"}</div>;
  }
  return (
    <ul className="mt-2 max-h-[60vh] space-y-1.5 overflow-y-auto" data-testid={`tasks-${panel}-list`}>
      {items.map((t) => (
        <li key={t.id} className="group flex items-start gap-2 rounded-xl px-2 py-2 transition-colors hover:bg-slate-50" data-testid={`task-item-${t.id}`}>
          <button
            data-testid={`task-complete-${t.id}`}
            onClick={() => doneMut.mutate(t.id)}
            className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 border-slate-300 text-transparent transition-colors hover:border-emerald-500 hover:text-emerald-500"
            aria-label="Mark complete"
          >
            <Check className="h-3 w-3" />
          </button>
          <div className="min-w-0 flex-1">
            <button onClick={() => onOpen(t)} className="block w-full text-left">
              <div className="flex items-center gap-1 truncate text-sm font-medium text-slate-800">
                {t.kind === "visit" && <MapPinned className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--brand-gold))]" />}
                <span className="truncate">{t.title}</span>
              </div>
              <div className={`text-[11px] ${showDate ? "text-rose-600" : "text-slate-500"}`}>
                {showDate ? fmtLocal(t.due_at) : fmtTime(t.due_at)}
                {t.owner_name && <span className="text-slate-400"> · {t.owner_name}</span>}
              </div>
            </button>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <ClientPhone task={t} />
              <RescheduleTaskButton task={t} onClick={onReschedule} />
            </div>
            <VisitHistory history={t.due_history} testid={`task-history-${t.id}`} label="reschedule history" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Legend({ cls, label }) {
  return <span className="inline-flex items-center gap-1.5"><span className={`h-3 w-3 rounded-sm border-l-[3px] ${cls}`} />{label}</span>;
}
